import React, { useCallback, useState } from 'react';

import { Box, Divider, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { checkCreationDetailsModified } from 'components/RabbitOKRCreationPanel/OKRCreationPanel';
import { useSelectOkrDrawerChangeDetails } from 'components/RabbitOKRCreationPanel/PrimaryPanel/V2/slice/okrDrawerChangeDetailsSlice';
import useOKRConfig from 'components/hooks/useOKRConfig';
import useUnsavedDataAlert from 'components/hooks/useUnsavedDataAlert';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { okrCreationPanelStateSelector } from 'selectors/okrCreationPanelSelector';

import { CollapseSidepanel } from 'assets/assetsV1/iconComponents';

import { OBJECTIVE_TYPES_MAP, OKR_TYPES } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';

import { DrawerFooter } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { CollaboratorsSection } from './sections/CollaboratorsSection';
import { DescriptionSection } from './sections/DescriptionSection';
import { ObjectiveLevelSection } from './sections/ObjectiveLevelSection';
import { OtherDetailsFooter, onOtherDetailsPanelCancel } from './sections/OtherDetailsFooter';
import { TagsSection } from './sections/TagsSection';
import { TimePeriod } from './sections/TimePeriod';
import { Tracking } from './sections/Tracking';
import { VisibilitySection } from './sections/VisibilitySection';

interface Props {
  isOpenAsParent: boolean;
}

const StyledCollapseSidepanel = styled(CollapseSidepanel)`
  color: #666666 !important;
`;

const OtherDetailsPanel = ({ isOpenAsParent }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { showUnsavedDataAlertDialog } = useUnsavedDataAlert();

  const [isErrorInForm, setIsErrorInForm] = useState(false);
  const originalDetails = useSelectOkrDrawerChangeDetails();

  const secondaryPanelDetailsObject = useSelector(kRDrawerSecondaryPanelSelector);
  const okrDetails = useSelector(okrCreationPanelStateSelector);
  const isUnsavedChangesInOtherDetailsPanel = checkCreationDetailsModified(
    okrDetails,
    originalDetails,
    originalDetails?.tagKeyList || []
  );
  const isObjective = okrDetails?.type === OKR_TYPES.OBJECTIVE;

  const { OBJECTIVE_TYPE } = useOKRConfig();

  const isDetailedObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.DETAILED.value;

  const showTrackingSection = !isObjective || isDetailedObjectiveType;

  const handleDiscardAndCancel = useCallback(() => {
    onOtherDetailsPanelCancel(
      dispatch,
      history,
      originalDetails,
      okrDetails,
      secondaryPanelDetailsObject?.details?.isOpenAsChild
    );
  }, [dispatch, history, okrDetails, originalDetails, secondaryPanelDetailsObject?.details?.isOpenAsChild]);

  const handleBackAndCancelButtonClick = () => {
    if (isUnsavedChangesInOtherDetailsPanel) {
      showUnsavedDataAlertDialog({
        onCancelCallback: () => {
          handleDiscardAndCancel();
        },
      });
    } else {
      handleDiscardAndCancel();
    }
  };

  return (
    // @ts-ignore
    <Flex backgroundColor="#FFFFFF" h="100%" flexDirection="column">
      <Box
        display="flex"
        w="100%"
        p="12px"
        mt="8px"
        alignItems="center"
        sx={{
          button: {
            height: '20px',
            width: '20px',
          },
        }}
        pointerEvents={isOpenAsParent ? 'none' : 'all'}
        opacity={isOpenAsParent ? '0.5' : 1}
      >
        <TextBodyMedium bold mr="auto" fontSize="14px" lineHeight="20px">
          Other Details
        </TextBodyMedium>
        <Button
          StartIcon={StyledCollapseSidepanel}
          variant="tertiary"
          onClick={() => {
            // @ts-ignore
            handleBackAndCancelButtonClick();
          }}
        />
      </Box>
      <Divider opacity={1} borderColor="#CFD7DF" mt="8px" mb="12px" marginLeft="auto" marginRight="auto" width="95%" />
      <Box padding={isMobileView() ? '8px 8px' : '8px 16px'} overflowX="auto" flex={1}>
        <TimePeriod />

        <VisibilitySection />

        <CollaboratorsSection />

        <ObjectiveLevelSection />

        {showTrackingSection && <Tracking />}
        <TagsSection />

        <DescriptionSection isLeftIcon setIsErrorInForm={setIsErrorInForm} />
      </Box>
      <DrawerFooter h="72px" backgroundColor="neutral.F5F7F9">
        <OtherDetailsFooter
          secondaryPanelDetails={secondaryPanelDetailsObject?.details}
          isErrorInForm={isErrorInForm}
          handleBackAndCancelButtonClick={handleBackAndCancelButtonClick}
        />
      </DrawerFooter>
    </Flex>
  );
};

export { OtherDetailsPanel };
